import { useParams, Link } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { contributionService } from '../../services/contributionService';
import { ContributionStatusBadge } from '../../components/common/StatusBadge';
import { formatCurrency, formatDate } from '../../utils/helpers';
import LoadingSpinner from '../../components/common/LoadingSpinner';

const SCHEDULE_STATUS_META = {
  paid:    { label: 'Paid',    bg: 'rgba(0,200,120,0.12)',  color: '#00C878' },
  overdue: { label: 'Overdue', bg: 'rgba(255,153,0,0.12)',  color: '#F5A623' },
  missed:  { label: 'Missed',  bg: 'rgba(255,71,87,0.12)',  color: '#FF4757' },
  partial: { label: 'Partial', bg: 'rgba(245,166,35,0.12)', color: '#F5A623' },
  pending: { label: 'Pending', bg: 'rgba(255,255,255,0.06)', color: 'var(--text-3)' },
  waived:  { label: 'Waived',  bg: 'rgba(91,142,255,0.12)', color: '#5B8EFF' },
};

function StatusPill({ status }) {
  const m = SCHEDULE_STATUS_META[status] || SCHEDULE_STATUS_META.pending;
  return (
    <span style={{ padding: '2px 8px', borderRadius: 99, fontSize: '0.65rem', fontWeight: 700, background: m.bg, color: m.color, whiteSpace: 'nowrap' }}>
      {m.label}
    </span>
  );
}

function ContributeModal({ plan, schedule, onClose }) {
  const qc = useQueryClient();
  const [amount, setAmount] = useState(schedule ? String(schedule.amount_due) : String(plan.contribution_amount));
  const [method, setMethod] = useState('bank_transfer');
  const [err, setErr] = useState('');

  const mutation = useMutation({
    mutationFn: (payload) => contributionService.contribute(plan.id, payload),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['contribution-schedule', plan.id] });
      qc.invalidateQueries({ queryKey: ['contribution-plan', plan.id] });
      qc.invalidateQueries({ queryKey: ['my-contribution-plans'] });
      qc.invalidateQueries({ queryKey: ['trust-score'] });
      onClose();
    },
    onError: (e) => setErr(e.response?.data?.message || 'Contribution failed.'),
  });

  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.65)', backdropFilter: 'blur(4px)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 }}>
      <div className="card" style={{ width: 420 }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.75rem' }}>
          <h2 style={{ fontSize: '1rem', fontWeight: 700 }}>Make Contribution</h2>
          <button className="btn btn-ghost" style={{ padding: '0.25rem 0.5rem' }} onClick={onClose}>✕</button>
        </div>
        {schedule && (
          <div style={{ background: 'var(--surface-alt)', borderRadius: 8, padding: '0.75rem', marginBottom: '1rem', fontSize: '0.8rem' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between' }}>
              <span style={{ color: 'var(--text-3)' }}>Installment #{schedule.installment_number}</span>
              <span style={{ color: 'var(--text-3)' }}>Due: {formatDate(schedule.due_date)}</span>
            </div>
          </div>
        )}
        <div className="form-group">
          <label className="form-label">Amount (₦)</label>
          <input className="form-input" type="number" min="1" value={amount} onChange={e => setAmount(e.target.value)} />
        </div>
        <div className="form-group">
          <label className="form-label">Payment Method</label>
          <select className="form-input" value={method} onChange={e => setMethod(e.target.value)}>
            <option value="bank_transfer">Bank Transfer</option>
            <option value="card">Card Payment</option>
            <option value="ussd">USSD</option>
            <option value="direct_debit">Direct Debit</option>
          </select>
        </div>
        {err && <div className="alert alert-error" style={{ marginBottom: '0.75rem' }}><span>⚠</span><span>{err}</span></div>}
        <div style={{ display: 'flex', gap: '0.75rem' }}>
          <button
            className="btn btn-primary" style={{ flex: 1 }}
            disabled={mutation.isPending || !amount || parseFloat(amount) <= 0}
            onClick={() => mutation.mutate({ amount: parseFloat(amount), method, schedule_id: schedule?.id })}
          >
            {mutation.isPending ? 'Processing…' : 'Confirm Contribution'}
          </button>
          <button className="btn btn-ghost" onClick={onClose}>Cancel</button>
        </div>
      </div>
    </div>
  );
}

function ProgressCardModal({ plan, onClose }) {
  const qc = useQueryClient();
  const [amount, setAmount] = useState('');
  const [err, setErr] = useState('');

  const { data: quote, isLoading: quoteLoading } = useQuery({
    queryKey: ['progress-card-quote', plan.id],
    queryFn: () => contributionService.getProgressCardQuote(plan.id).then(r => r.data.data),
  });

  const mutation = useMutation({
    mutationFn: (payload) => contributionService.requestProgressCard(plan.id, payload),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['contribution-plan', plan.id] });
      qc.invalidateQueries({ queryKey: ['trust-score'] });
      onClose();
    },
    onError: (e) => setErr(e.response?.data?.message || 'Request failed.'),
  });

  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.65)', backdropFilter: 'blur(4px)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 }}>
      <div className="card" style={{ width: 420 }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.75rem' }}>
          <h2 style={{ fontSize: '1rem', fontWeight: 700 }}>Progress Card Withdrawal</h2>
          <button className="btn btn-ghost" style={{ padding: '0.25rem 0.5rem' }} onClick={onClose}>✕</button>
        </div>
        {quoteLoading ? <LoadingSpinner /> : quote && (
          !quote.eligible ? (
            <div className="alert alert-warning"><span>⚠</span><span>Trust Score too low. You need {quote.min_score_required}, currently at {quote.trust_score}.</span></div>
          ) : (
            <>
              <div style={{ background: 'var(--surface-alt)', borderRadius: 8, padding: '0.75rem', marginBottom: '1rem', fontSize: '0.8rem' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 4 }}>
                  <span style={{ color: 'var(--text-2)' }}>Plan Balance</span>
                  <span style={{ fontWeight: 700 }}>{formatCurrency(quote.plan_balance)}</span>
                </div>
                <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                  <span style={{ color: 'var(--text-2)' }}>Max Withdrawable (40%)</span>
                  <span style={{ fontWeight: 700, color: '#00d9a5' }}>{formatCurrency(quote.max_withdrawal_amount)}</span>
                </div>
              </div>
              <div className="form-group">
                <label className="form-label">Withdrawal Amount (₦)</label>
                <input className="form-input" type="number" min="1" max={quote.max_withdrawal_amount} value={amount} onChange={e => setAmount(e.target.value)} />
              </div>
              {err && <div className="alert alert-error" style={{ marginBottom: '0.75rem' }}><span>⚠</span><span>{err}</span></div>}
              <div style={{ display: 'flex', gap: '0.75rem' }}>
                <button
                  className="btn btn-primary" style={{ flex: 1 }}
                  disabled={mutation.isPending || !amount || parseFloat(amount) <= 0}
                  onClick={() => mutation.mutate({ amount: parseFloat(amount) })}
                >
                  {mutation.isPending ? 'Processing…' : 'Request Withdrawal'}
                </button>
                <button className="btn btn-ghost" onClick={onClose}>Cancel</button>
              </div>
            </>
          )
        )}
      </div>
    </div>
  );
}

function PlanAdvanceModal({ plan, onClose }) {
  const qc = useQueryClient();
  const [err, setErr] = useState('');

  const { data: quote, isLoading: quoteLoading } = useQuery({
    queryKey: ['plan-advance-quote', plan.id],
    queryFn: () => contributionService.getPlanAdvanceQuote(plan.id).then(r => r.data.data),
  });

  const mutation = useMutation({
    mutationFn: () => contributionService.requestPlanAdvance(plan.id, {}),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['contribution-plan', plan.id] });
      onClose();
    },
    onError: (e) => setErr(e.response?.data?.message || 'Request failed.'),
  });

  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.65)', backdropFilter: 'blur(4px)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 }}>
      <div className="card" style={{ width: 420 }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.75rem' }}>
          <h2 style={{ fontSize: '1rem', fontWeight: 700 }}>Plan Advance</h2>
          <button className="btn btn-ghost" style={{ padding: '0.25rem 0.5rem' }} onClick={onClose}>✕</button>
        </div>
        {quoteLoading ? <LoadingSpinner /> : quote && (
          !quote.eligible ? (
            <div className="alert alert-warning"><span>⚠</span><span>Trust Score too low. You need {quote.min_score_required}, currently at {quote.trust_score}.</span></div>
          ) : (
            <>
              <div className="alert alert-info" style={{ marginBottom: '1rem' }}>
                <span>ℹ</span><span>This ends your plan early. A fixed fee applies — no compounding interest.</span>
              </div>
              <div style={{ background: 'var(--surface-alt)', borderRadius: 8, padding: '0.75rem', marginBottom: '1.25rem', fontSize: '0.8rem' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 4 }}>
                  <span style={{ color: 'var(--text-2)' }}>Remaining Goal</span>
                  <span style={{ fontWeight: 700 }}>{formatCurrency(quote.remaining_goal_amount)}</span>
                </div>
                <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 4 }}>
                  <span style={{ color: '#F5A623' }}>Fee</span>
                  <span style={{ fontWeight: 700, color: '#F5A623' }}>{formatCurrency(quote.fee_amount)}</span>
                </div>
                <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: 6, paddingTop: 6, borderTop: '1px solid var(--border)' }}>
                  <span style={{ color: 'var(--text-2)', fontWeight: 600 }}>You'll Receive</span>
                  <span style={{ fontWeight: 700, color: '#00d9a5', fontSize: '0.9rem' }}>{formatCurrency(quote.payout_amount)}</span>
                </div>
              </div>
              {err && <div className="alert alert-error" style={{ marginBottom: '0.75rem' }}><span>⚠</span><span>{err}</span></div>}
              <div style={{ display: 'flex', gap: '0.75rem' }}>
                <button className="btn btn-primary" style={{ flex: 1 }} disabled={mutation.isPending} onClick={() => mutation.mutate()}>
                  {mutation.isPending ? 'Submitting…' : 'Request Advance'}
                </button>
                <button className="btn btn-ghost" onClick={onClose}>Cancel</button>
              </div>
            </>
          )
        )}
      </div>
    </div>
  );
}

export default function ContributionPlanDetail() {
  const { id: planId } = useParams();
  const [modal, setModal] = useState(null); // { type: 'contribute'|'progress-card'|'advance', schedule? }

  const { data: plan, isLoading: planLoading } = useQuery({
    queryKey: ['contribution-plan', planId],
    queryFn: () => contributionService.getPlan(planId).then(r => r.data.data),
  });

  const { data: scheduleData, isLoading: scheduleLoading } = useQuery({
    queryKey: ['contribution-schedule', planId],
    queryFn: () => contributionService.getSchedule(planId).then(r => r.data.data),
  });

  const schedules = scheduleData?.results || [];
  const paidCount = schedules.filter(s => s.status === 'paid').length;

  if (planLoading) return <LoadingSpinner />;
  if (!plan) return <p style={{ color: 'var(--text-3)' }}>Plan not found.</p>;

  const progress = plan.goal_amount > 0 ? Math.min(100, (plan.total_contributed / plan.goal_amount) * 100) : 0;
  const nextDue = schedules.find(s => ['pending', 'overdue', 'partial'].includes(s.status));

  return (
    <div>
      <div className="page-header">
        <div>
          <div style={{ fontSize: '0.78rem', color: 'var(--text-3)', marginBottom: '0.2rem' }}>
            <Link to="/contributions" style={{ color: 'var(--text-3)', textDecoration: 'none' }}>← My Savings Plans</Link>
          </div>
          <h1>{plan.goal_name}</h1>
          <p>{plan.reference}</p>
        </div>
        <ContributionStatusBadge status={plan.status} />
      </div>

      <div className="grid-3" style={{ marginBottom: '1.25rem' }}>
        <div className="stat-card">
          <div className="stat-value">{formatCurrency(plan.balance)}</div>
          <div className="stat-label">Current Balance</div>
        </div>
        <div className="stat-card">
          <div className="stat-value">{formatCurrency(plan.goal_amount)}</div>
          <div className="stat-label">Goal</div>
        </div>
        <div className="stat-card">
          <div className="stat-value">{formatCurrency(plan.contribution_amount)}</div>
          <div className="stat-label">Per {plan.cycle}</div>
        </div>
      </div>

      <div className="card" style={{ padding: '0.875rem 1.125rem', marginBottom: '1.25rem' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.75rem', marginBottom: '0.3rem' }}>
          <span style={{ color: 'var(--text-2)' }}>{paidCount} of {schedules.length} contributions made</span>
          <strong style={{ color: '#1596E0' }}>{Math.round(progress)}%</strong>
        </div>
        <div style={{ height: 7, borderRadius: 4, background: 'var(--border)', overflow: 'hidden' }}>
          <div style={{ height: '100%', width: `${progress}%`, background: 'linear-gradient(90deg,#1596E0,#0B6FB0)', borderRadius: 4, transition: 'width 0.4s' }} />
        </div>
      </div>

      {plan.status === 'active' && (
        <div style={{ display: 'flex', gap: '0.75rem', flexWrap: 'wrap', marginBottom: '1.5rem' }}>
          <button className="btn btn-primary" onClick={() => setModal({ type: 'contribute', schedule: nextDue })}>Make Contribution</button>
          <button className="btn btn-secondary" onClick={() => setModal({ type: 'progress-card' })}>Progress Card</button>
          <button className="btn btn-secondary" onClick={() => setModal({ type: 'advance' })}>Plan Advance</button>
        </div>
      )}

      <div className="card" style={{ padding: 0 }}>
        {scheduleLoading ? (
          <div style={{ padding: '2rem' }}><LoadingSpinner /></div>
        ) : (
          <div className="table-wrapper">
            <table style={{ fontSize: '0.8rem' }}>
              <thead>
                <tr>
                  {['#', 'Due Date', 'Amount Due', 'Paid', 'Balance', 'Status', ''].map(h => (
                    <th key={h} style={{ padding: '0.5rem 0.75rem', fontSize: '0.65rem', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.05em', color: 'var(--text-3)' }}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {schedules.map(s => (
                  <tr key={s.id} style={{ borderBottom: '1px solid rgba(255,255,255,0.04)' }}>
                    <td style={{ padding: '0.5rem 0.75rem', textAlign: 'center', fontWeight: 800, color: s.status === 'paid' ? '#00C878' : '#1596E0' }}>
                      {s.status === 'paid' ? '✓' : s.installment_number}
                    </td>
                    <td style={{ padding: '0.5rem 0.75rem' }}>{formatDate(s.due_date)}</td>
                    <td style={{ padding: '0.5rem 0.75rem', fontWeight: 700 }}>{formatCurrency(s.amount_due)}</td>
                    <td style={{ padding: '0.5rem 0.75rem', color: parseFloat(s.amount_paid) > 0 ? '#00C878' : 'var(--text-3)' }}>
                      {parseFloat(s.amount_paid) > 0 ? formatCurrency(s.amount_paid) : '—'}
                    </td>
                    <td style={{ padding: '0.5rem 0.75rem' }}>{formatCurrency(s.balance)}</td>
                    <td style={{ padding: '0.5rem 0.75rem' }}><StatusPill status={s.status} /></td>
                    <td style={{ padding: '0.5rem 0.75rem' }}>
                      {['pending', 'overdue', 'partial'].includes(s.status) && plan.status === 'active' && (
                        <button className="btn btn-primary" style={{ fontSize: '0.7rem', padding: '0.25rem 0.6rem' }} onClick={() => setModal({ type: 'contribute', schedule: s })}>
                          Pay
                        </button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {modal?.type === 'contribute' && <ContributeModal plan={plan} schedule={modal.schedule} onClose={() => setModal(null)} />}
      {modal?.type === 'progress-card' && <ProgressCardModal plan={plan} onClose={() => setModal(null)} />}
      {modal?.type === 'advance' && <PlanAdvanceModal plan={plan} onClose={() => setModal(null)} />}
    </div>
  );
}
